import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../component-styles/ReportsShared.css';
import API_BASE from '../../utils/api.js';
import { exportRowsToExcel } from '../../utils/excelExport.js';


function formatDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}

function getDefaultDateRange() {
  const year = new Date().getFullYear();
  return { start: `${year}-01-01`, end: `${year}-12-31` };
}

function normalizeProject(raw) {
  return {
    id: String(raw.projectId || raw.id || raw.name || ''),
    name: raw.projectNickname || raw.name || raw.title || 'Untitled Project',
  };
}

export default function TaskCompletionReportPage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  const [projects, setProjects] = useState([]);
  const [teamMap, setTeamMap] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');

  useEffect(() => {
    async function loadProjects() {
      if (!userId) {
        setProjects([]);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/projects/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const arr = Array.isArray(data) ? data : data?.Items || data?.items || [];
        setProjects(arr.map(normalizeProject));
      } catch (err) {
        console.error('Failed to load projects', err);
        setProjects([]);
      }
    }

    async function loadTeam() {
      if (!userId) return;
      try {
        const res = await fetch(`${API_BASE}/api/settings/${encodeURIComponent(userId)}`);
        if (!res.ok) return;
        const data = await res.json();
        const team = Array.isArray(data?.team) ? data.team : [];
        const map = {};
        team.forEach((member) => {
          if (member?.email) map[member.email] = member.name || member.email;
        });
        setTeamMap(map);
      } catch {
        setTeamMap({});
      }
    }

    loadProjects();
    loadTeam();
  }, [userId]);

  useEffect(() => {
    async function loadTodos() {
      if (!userId) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/todos/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setRows(
          list.map((todo) => ({
            todoId: todo.todoId || todo.id || '',
            title: todo.title || '',
            notes: todo.notes || '',
            completed: Boolean(todo.completed),
            dueDate: todo.dueDate || '',
            priority: todo.priority || 'normal',
            projectId: todo.projectId ? String(todo.projectId) : '',
            projectName: todo.projectName || '',
            assignees: Array.isArray(todo.assignees) ? todo.assignees : [],
          }))
        );
      } catch (err) {
        console.error('Failed to load tasks', err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    }

    loadTodos();
  }, [userId]);

  const projectMap = useMemo(() => {
    return projects.reduce((acc, p) => {
      acc[String(p.id)] = p.name;
      return acc;
    }, {});
  }, [projects]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (projectFilter !== 'all' && r.projectId !== projectFilter) return false;
      if (statusFilter === 'open' && r.completed) return false;
      if (statusFilter === 'completed' && !r.completed) return false;
      if (assigneeFilter !== 'all' && !(r.assignees || []).includes(assigneeFilter)) return false;
      const date = String(r.dueDate || '').slice(0, 10);
      if (startDate && date && date < startDate) return false;
      if (endDate && date && date > endDate) return false;
      return true;
    });
  }, [rows, projectFilter, statusFilter, assigneeFilter, startDate, endDate]);

  const totals = useMemo(() => {
    const total = filteredRows.length;
    const completed = filteredRows.filter((r) => r.completed).length;
    return { total, completed, open: total - completed };
  }, [filteredRows]);

  function exportExcel() {
    const headers = ['Project', 'Task', 'Due Date', 'Priority', 'Assignees', 'Status', 'Notes'];
    const rowsForExport = filteredRows.map((r) => [
      projectMap[r.projectId] || r.projectName || 'Untitled Project',
      r.title || '',
      r.dueDate || '',
      r.priority || '',
      (r.assignees || [])
        .map((a) => teamMap[a] || a)
        .filter(Boolean)
        .join('; '),
      r.completed ? 'Completed' : 'Open',
      r.notes || '',
    ]);

    exportRowsToExcel({
      filename: 'task-completion-report.xlsx',
      sheetName: 'Task Completion',
      headers,
      rows: rowsForExport,
    });
  }

  return (
    <div className="report-shell">
      <div className="report-shell-inner">
        <div className="report-header">
          <div className="report-title">
            <h1>Task Completion Report</h1>
            <div className="muted">
              {startDate} - {endDate}
            </div>
          </div>

          <div className="report-actions">
            <button className="report-btn-secondary" onClick={() => navigate('/reports')}>
              Back
            </button>
            <button className="report-btn-primary" onClick={exportExcel}>
              Export Excel
            </button>
          </div>
        </div>

        <div className="report-filters">
          <select
            className="report-select"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <option value="all">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          <select
            className="report-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="completed">Completed</option>
          </select>

          <select
            className="report-select"
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
          >
            <option value="all">All Team Members</option>
            {Object.entries(teamMap).map(([email, name]) => (
              <option key={email} value={email}>
                {name}
              </option>
            ))}
          </select>

          <input
            className="report-input"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            className="report-input"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="report-card">
          <div className="report-metrics">
            <div className="report-metric">
              <div className="report-metric-value">{totals.total}</div>
              <div className="report-metric-label">Total Tasks</div>
            </div>
            <div className="report-metric">
              <div className="report-metric-value">{totals.completed}</div>
              <div className="report-metric-label">Completed</div>
            </div>
            <div className="report-metric">
              <div className="report-metric-value">{totals.open}</div>
              <div className="report-metric-label">Open</div>
            </div>
          </div>
        </div>

        <div className="report-table-wrap">
          {loading ? (
            <div className="report-empty">Loading tasks...</div>
          ) : filteredRows.length === 0 ? (
            <div className="report-empty">No tasks found.</div>
          ) : (
            <table className="report-table" role="table" aria-label="Task completion">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Task</th>
                  <th>Due Date</th>
                  <th>Priority</th>
                  <th>Assignees</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.todoId}>
                    <td>{projectMap[r.projectId] || r.projectName || 'Untitled Project'}</td>
                    <td>{r.title}</td>
                    <td>{formatDate(r.dueDate)}</td>
                    <td>{r.priority}</td>
                    <td>
                      {(r.assignees || [])
                        .map((a) => teamMap[a] || a)
                        .filter(Boolean)
                        .join(', ') || '-'}
                    </td>
                    <td>
                      <span className="report-badge">
                        {r.completed ? 'Completed' : 'Open'}
                      </span>
                    </td>
                    <td>{r.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
